/** What a finished edit of the target box means.
 *
 *  A module rather than a branch inside the cell, for the reason watchSort.ts
 *  is one: this suite runs under `environment: "node"` and has no DOM to
 *  render a cell into, so a decision left in the component is a decision no
 *  test can reach. Everything the browser knows arrives as arguments and
 *  everything the cell must do comes back as a result.
 */

/** `keep` — nothing changed, so do not write. `reset` — put the stored value
 *  back on screen; whatever is in the box is not a target this server would
 *  take. `save` — PUT it, `null` meaning "clear the target". */
export type TargetCommit =
  | { kind: "keep" }
  | { kind: "reset" }
  | { kind: "save"; target: number | null };

/** `badInput` is `input.validity.badInput`, and it has to be asked for.
 *
 *  A `type="number"` input whose content the browser could not sanitise —
 *  "1e", "4-2", "--" — reports `value === ""`, which is exactly what an empty
 *  box reports. Without this flag the two are indistinguishable, so a typo on
 *  the way to a number reads as "clear the target" and silently deletes one
 *  the user never asked to delete. The non-integer guard below never sees it,
 *  because the characters never arrive as a value at all.
 */
export function commitTarget(
  draft: string, badInput: boolean, stored: number | null,
): TargetCommit {
  if (badInput) return { kind: "reset" };

  const typed = draft.trim();
  const target = typed === "" ? null : Number(typed);
  // An unchanged box is not an edit. Without this, tabbing across the table
  // would PUT every row it passed through.
  if (target === stored) return { kind: "keep" };
  if (target !== null && (!Number.isInteger(target) || target <= 0)) {
    // isInteger, not isFinite: target_price is an int with gt=0 on the server,
    // so a typed 4200.5 is a 422 rather than a target. Not one this server
    // will take, so put the stored value back rather than leaving a number on
    // screen that was never saved.
    return { kind: "reset" };
  }
  return { kind: "save", target };
}
